import {createContext, use, useRef, type RefObject, type ReactNode} from 'react'
import {createStore, useStore} from 'zustand'
import {devtools} from 'zustand/middleware'
import {createIssueCommentSlice, type IssueCommentSlice} from './slices/issue-comments'
import {
  createLayoutCurrentUserSlice,
  type LayoutCurrentUserSlice,
  type LayoutCurrentUserSliceOpts,
} from './slices/layout-current-user'
import {
  createLayoutPullRequestSlice,
  type LayoutPullRequestSlice,
  type LayoutPullRequestSliceOpts,
} from './slices/layout-pull-request'
import {createMarkersSlice, type MarkersInfo, type MarkersSlice, type MarkersSliceOpts} from './slices/markers'
import {
  createPendingReviewSlice,
  type PendingReview,
  type PendingReviewSlice,
  type PendingReviewSliceOpts,
} from './slices/pending-review'
import {createPreferencesSlice, type PreferencesSlice, type PreferencesSliceOpts} from './slices/preferences'
import {
  createCommentPersistenceSlice,
  type CommentPersistenceSlice,
  type CommentPersistenceSliceOpts,
} from './slices/comment-persistence'
import {
  createReviewSummarySlice,
  type ReviewSummariesSliceOpts,
  type ReviewSummarySlice,
} from './slices/review-summaries'
import {
  createCommentsPanelViewSlice,
  type CommentsPanelViewSlice,
  type CommentsPanelViewSliceOpts,
} from './slices/comments-panel-view'
import {createMarkerCountsSlice, type MarkerCountsSlice} from './slices/marker-counts'
import {createComparisonOutdatedSlice, type ComparisonOutdatedSlice} from './slices/comparison-outdated'
import {createUnifiedBatchSlice, type UnifiedBatchItem, type UnifiedBatchSlice} from './slices/unified-batch'
import {loadUnifiedBatchFromStorage} from '../helpers/unified-batch-storage'
import {usePersistUnifiedBatch} from './use-persist-unified-batch'
import type {HeaderPageData} from '../page-data/payloads/header'
import {buildDiffPathOrderMap, buildNormalizedMarkers, buildPendingReview} from '../helpers/diff-helpers'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import {isFeatureEnabled} from '@github-ui/feature-flags'

/** Input type for the pull request data passed to LayoutStoreProvider */
export type LayoutPullRequest = Pick<
  HeaderPageData['pullRequest'],
  'id' | 'number' | 'author' | 'headSha' | 'headRepositoryOwnerLogin' | 'headRepositoryName'
> & {
  title?: HeaderPageData['pullRequest']['title']
}

/** Shared repository shape used by the layout pull-request slice and LayoutStoreProvider. */
export type LayoutBaseRepository = Pick<HeaderPageData['repository'], 'id' | 'ownerLogin' | 'name'> &
  Partial<Pick<HeaderPageData['repository'], 'currentUserCanPush' | 'public' | 'private'>>

/** Shared notice shape used by the layout current-user slice and LayoutStoreProvider. */
export type LayoutUserNotice = NonNullable<HeaderPageData['user']['notices']>[number]

/** Shared current-user shape used by the layout current-user slice and LayoutStoreProvider. */
export type LayoutCurrentUser = {
  canComment: boolean
  canEditTitle: boolean
  canFixWithCopilot?: boolean | null
  notices: LayoutUserNotice[]
}

/** Minimal shape accepted by LayoutStoreProvider's `initData` prop. */
export interface LayoutStoreInitData {
  pullRequest: LayoutPullRequest
  repository: LayoutBaseRepository
  user: Pick<HeaderPageData['user'], 'canComment' | 'canEditTitle' | 'canFixWithCopilot' | 'notices'>
  viewerPendingReview?: PendingReview
}

export type NavigateToCommentFn = (commentId: number) => void
export type NavigateToAnnotationFn = (annotationId: number) => void

/** Callbacks registered by the files-changed route into the LayoutStore.
 *  These are non-null only when the files-changed route is mounted and allow
 *  layout-level components (e.g. side panels) to interact with the diff view. */
export interface FilesChangedCallbacks {
  navigateToComment: NavigateToCommentFn
  navigateToAnnotation: NavigateToAnnotationFn
  onCommentThreadDeleted: (args: {path: string; threadID: string}) => void
}

export type LayoutStoreState = IssueCommentSlice &
  LayoutCurrentUserSlice &
  LayoutPullRequestSlice &
  MarkersSlice &
  PendingReviewSlice &
  PreferencesSlice &
  CommentPersistenceSlice &
  CommentsPanelViewSlice &
  ReviewSummarySlice &
  MarkerCountsSlice &
  ComparisonOutdatedSlice &
  UnifiedBatchSlice & {
    /** True once the store has been seeded with markers/pendingReview.
     *  Prevents stale re-hydration when PullRequestStoreProvider remounts. */
    layoutStoreSeeded: boolean
    /** Map from file path to its position index in the diff order.
     *  Seeded from files-changed route data or lazy-fetched via file_tree endpoint. */
    diffPathOrderMap: Map<string, number>
    /** Callbacks registered by the files-changed route. Null when not on files-changed. */
    filesChangedCallbacks: FilesChangedCallbacks | null
    layoutStoreActions: {
      hydrateMarkers: (markers: MarkersInfo, annotationsPageInfo?: MarkersSlice['annotationsPageInfo']) => void
      hydratePendingReview: (pendingReview: PendingReview) => void
      hydratePreferences: (preferences: PreferencesSliceOpts) => void
      hydrateDiffPathOrderMap: (diffPathOrderMap: Map<string, number>) => void
      registerFilesChangedCallbacks: (callbacks: FilesChangedCallbacks) => void
      unregisterFilesChangedCallbacks: () => void
    }
  }

type LayoutStoreInitialState = MarkersSliceOpts &
  LayoutCurrentUserSliceOpts &
  LayoutPullRequestSliceOpts &
  PendingReviewSliceOpts &
  PreferencesSliceOpts &
  CommentPersistenceSliceOpts &
  CommentsPanelViewSliceOpts &
  ReviewSummariesSliceOpts & {
    layoutStoreSeeded: boolean
    diffPathOrderMap: Map<string, number>
    initialUnifiedBatch?: UnifiedBatchItem[]
    /** When true, wire `markComparisonOutdated` to cascade into the unified-batch slice.
     *  Gated by `unified_batch_pr_comments` so we don't perturb prod behavior while the
     *  flag is off. */
    unifiedBatchEnabled: boolean
  }

export type LayoutStoreInstance = ReturnType<typeof createLayoutStore>

const LayoutStoreContext = createContext<LayoutStoreInstance | null>(null)

function getInitialUserNotices(data: {
  user: {notices?: LayoutUserNotice[]}
  userNotices?: LayoutUserNotice[]
}): LayoutUserNotice[] {
  return data.userNotices ?? data.user.notices ?? []
}

export function useLayoutStore<T>(selector: (state: LayoutStoreState) => T): T {
  const store = use(LayoutStoreContext)
  if (!store) throw new Error('Missing LayoutStoreProvider in the tree')
  return useStore(store, selector)
}

export interface LayoutStoreProviderProps {
  children: ReactNode
  initData: LayoutStoreInitData
  basePath: string
  /** Ref populated by FilesChangedDataCapture (rendered as an earlier sibling) with
   *  child-route data so the store can be created with real markers/pendingReview
   *  on the very first render — critical for SSR. */
  initialFilesDataRef?: RefObject<FilesRoutePayload | undefined>
  /** Shared ref that tracks whether the store has been seeded (SSR or soft-nav).
   *  Set to true when the SSR path seeds, read by LayoutStoreSeeder to avoid re-seeding. */
  seededRef?: RefObject<boolean>
}

export function LayoutStoreProviderWrapper({
  shouldAddLayoutStoreWrapper,
  children,
  initData,
  basePath,
}: {
  shouldAddLayoutStoreWrapper: boolean
  children: ReactNode
  initData: LayoutStoreInitData
  basePath: string
}) {
  if (!shouldAddLayoutStoreWrapper) {
    return <>{children}</>
  }
  return (
    <LayoutStoreProvider initData={initData} basePath={basePath}>
      {children}
    </LayoutStoreProvider>
  )
}
export function LayoutStoreProvider({
  children,
  initData,
  basePath,
  initialFilesDataRef,
  seededRef,
}: LayoutStoreProviderProps) {
  const {pullRequest, repository, user, viewerPendingReview} = initData
  const storeRef = useRef<LayoutStoreInstance | null>(null)

  if (storeRef.current === null) {
    // Read the ref populated by FilesChangedDataCapture (sibling rendered before this component).
    // React renders siblings in order, so the ref is already populated during SSR.
    // eslint-disable-next-line react-hooks/refs
    const filesData = initialFilesDataRef?.current
    const initialMarkers = filesData ? (buildNormalizedMarkers(filesData.markers, filesData.diffSummaries) ?? {}) : {}
    const initialPendingReview = filesData
      ? buildPendingReview(filesData.viewerPendingReview)
      : viewerPendingReview
        ? buildPendingReview(viewerPendingReview)
        : {comments: []}
    const initialCommentsPreference = filesData
      ? filesData.user.viewSettings.commentsPreference
      : CommentsPreference.Visible
    const initialUserNotices = getInitialUserNotices(filesData ?? initData)
    const initialDiffPathOrderMap = filesData ? buildDiffPathOrderMap(filesData.diffSummaries) : new Map()

    const unifiedBatchEnabled = isFeatureEnabled('unified_batch_pr_comments')
    // Hydrate from sessionStorage at layout init so the batch survives across
    // tab navigation (Conversation/Checks/etc.) — independent of the files-changed payload.
    const initialUnifiedBatch: UnifiedBatchItem[] = unifiedBatchEnabled
      ? loadUnifiedBatchFromStorage(basePath, pullRequest.headSha)
      : []

    // Mark as seeded so LayoutStoreSeeder won't re-seed on subsequent soft-navs
    // eslint-disable-next-line react-hooks/refs
    if (filesData && seededRef) seededRef.current = true

    // eslint-disable-next-line react-hooks/refs
    const wasSeeded = !!filesData

    storeRef.current = createLayoutStore({
      markers: initialMarkers,
      annotationsPageInfo: filesData?.markers?.annotationsPageInfo,
      pendingReview: initialPendingReview,
      storedCommentsByFile: {},
      layoutStoreSeeded: wasSeeded,
      commentsPreference: initialCommentsPreference,
      diffPathOrderMap: initialDiffPathOrderMap,
      layoutPullRequest: {
        pullRequest: {
          id: pullRequest.id,
          number: pullRequest.number,
          author: pullRequest.author,
          headSha: pullRequest.headSha,
          headRepositoryOwnerLogin: pullRequest.headRepositoryOwnerLogin,
          headRepositoryName: pullRequest.headRepositoryName,
          title: pullRequest.title,
        },
        basePath,
        baseRepository: {
          id: repository.id,
          ownerLogin: repository.ownerLogin,
          name: repository.name,
          currentUserCanPush: repository.currentUserCanPush,
          public: repository.public,
          private: repository.private,
        },
      },
      layoutCurrentUser: {
        canComment: user.canComment ?? false,
        canEditTitle: user.canEditTitle ?? false,
        canFixWithCopilot: user.canFixWithCopilot,
        notices: initialUserNotices,
      },
      initialUnifiedBatch,
      unifiedBatchEnabled,
    })
  }

  // Persist unified-batch mutations to sessionStorage.
  usePersistUnifiedBatch(storeRef, basePath, pullRequest.headSha)

  // We disable ESLint refs here as we need our Zustand stores to be a stable reference
  // Using a useState to store our Zustand store will cause unnecessary re-renders and downstream performance issues
  // eslint-disable-next-line react-hooks/refs
  return <LayoutStoreContext value={storeRef.current}>{children}</LayoutStoreContext>
}

const createLayoutStore = (initState: LayoutStoreInitialState) => {
  return createStore<LayoutStoreState>()(
    devtools(
      (...a) => ({
        ...createIssueCommentSlice()(...a),
        ...createMarkersSlice(initState)(...a),
        ...createPendingReviewSlice(initState)(...a),
        ...createPreferencesSlice(initState)(...a),
        ...createCommentPersistenceSlice(initState)(...a),
        ...createCommentsPanelViewSlice(initState)(...a),
        ...createLayoutCurrentUserSlice(initState)(...a),
        ...createLayoutPullRequestSlice(initState)(...a),
        ...createReviewSummarySlice(initState)(...a),
        ...createMarkerCountsSlice()(...a),
        ...createComparisonOutdatedSlice(...a),
        ...createUnifiedBatchSlice({
          initialItems: initState.initialUnifiedBatch,
          enabled: initState.unifiedBatchEnabled,
        })(...a),
        layoutStoreSeeded: initState.layoutStoreSeeded,
        diffPathOrderMap: initState.diffPathOrderMap,
        filesChangedCallbacks: null,
        layoutStoreActions: {
          hydrateMarkers: (markers: MarkersInfo, annotationsPageInfo?: MarkersSlice['annotationsPageInfo']) => {
            const [set] = a
            set({markers, annotationsPageInfo}, undefined, 'hydrateMarkers')
          },
          hydratePendingReview: (pendingReview: PendingReview) => {
            const [set] = a
            set({pendingReview}, undefined, 'hydratePendingReview')
          },
          hydratePreferences: (preferences: PreferencesSliceOpts) => {
            const [set] = a
            set(
              {commentsPreference: preferences.commentsPreference ?? CommentsPreference.Visible},
              undefined,
              'hydratePreferences',
            )
          },
          hydrateDiffPathOrderMap: (diffPathOrderMap: Map<string, number>) => {
            const [set] = a
            set({diffPathOrderMap}, undefined, 'hydrateDiffPathOrderMap')
          },
          registerFilesChangedCallbacks: (callbacks: FilesChangedCallbacks) => {
            const [set] = a
            set({filesChangedCallbacks: callbacks}, undefined, 'registerFilesChangedCallbacks')
          },
          unregisterFilesChangedCallbacks: () => {
            const [set] = a
            set({filesChangedCallbacks: null}, undefined, 'unregisterFilesChangedCallbacks')
          },
        },
      }),
      {name: 'layout-store'},
    ),
  )
}

/**
 * Returns the raw zustand store instance for imperative access (e.g. from PullRequestStoreProvider).
 * Prefer useLayoutStore with selectors in components.
 */
export function useLayoutStoreInstance(): LayoutStoreInstance {
  const store = use(LayoutStoreContext)
  if (!store) throw new Error('Missing LayoutStoreProvider in the tree')
  return store
}
